import React from 'react';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="small" clickable={false} />
          <a
            href="/"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              Botibonga ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use Botibonga Studio.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-lg font-medium text-gray-800 mb-3">2.1 Personal Information</h3>
            <p className="text-gray-600 mb-4">When you create an account, we collect:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Name and email address (via Google authentication)</li>
              <li>Profile picture (from your Google account)</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">2.2 Business Information</h3>
            <p className="text-gray-600 mb-4">When you create chatbots, we collect:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Business name and description</li>
              <li>WhatsApp phone number</li>
              <li>Knowledge base documents you upload</li>
              <li>Chatbot configuration settings</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">2.3 Conversation Data</h3>
            <p className="text-gray-600 mb-4">We collect and store:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Messages exchanged between your chatbot and customers</li>
              <li>Customer phone numbers and names (as provided)</li>
              <li>Lead information captured during conversations</li>
              <li>Appointment booking details</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">2.4 Usage Data</h3>
            <p className="text-gray-600 mb-4">We automatically collect:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Log data (IP address, browser type, pages visited)</li>
              <li>Analytics data (feature usage, conversation metrics)</li>
              <li>Device information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use the collected information to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Provide and maintain our Service</li>
              <li>Process and deliver AI-powered chatbot responses</li>
              <li>Store and retrieve your knowledge base for context</li>
              <li>Track leads and appointments on your behalf</li>
              <li>Send service-related notifications</li>
              <li>Improve and optimize our Service</li>
              <li>Provide customer support</li>
              <li>Detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">We may share your information with:</p>

            <h3 className="text-lg font-medium text-gray-800 mb-3">4.1 Service Providers</h3>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li><strong>AI Providers:</strong> We send conversation data to AI services (OpenAI, Anthropic, Google, etc.) to generate responses</li>
              <li><strong>Cloud Infrastructure:</strong> Firebase/Google Cloud for data storage</li>
              <li><strong>WhatsApp:</strong> Message delivery through WhatsApp Business API</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-3">4.2 Legal Requirements</h3>
            <p className="text-gray-600 mb-4">
              We may disclose information if required by law, court order, or government request.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-600 mb-4">We implement appropriate security measures including:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>SSL/TLS encryption for data in transit</li>
              <li>Encrypted storage for sensitive data</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
            </ul>
            <p className="text-gray-600 mb-4">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain your data for as long as your account is active. Upon account deletion:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Personal account data is deleted within 30 days</li>
              <li>Conversation logs are deleted within 90 days</li>
              <li>Anonymized analytics data may be retained indefinitely</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Export:</strong> Request your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p className="text-gray-600 mb-4">
              To exercise these rights, contact us at sales@botibonga.com
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking</h2>
            <p className="text-gray-600 mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-gray-600 mb-4 space-y-2">
              <li>Maintain your login session</li>
              <li>Remember your preferences</li>
              <li>Analyze usage patterns</li>
            </ul>
            <p className="text-gray-600 mb-4">
              You can control cookies through your browser settings, but some features may not work properly without them.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-600 mb-4">
              Our Service is not intended for users under 18 years of age. We do not knowingly collect
              personal information from children. If you believe we have collected data from a child,
              please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
            <p className="text-gray-600 mb-4">
              Your information may be transferred to and processed in countries other than your own.
              We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-600 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of significant changes
              via email or through the Service. Your continued use after changes constitutes acceptance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-gray-600">
              <strong>Email:</strong> sales@botibonga.com
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Botibonga. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
